import { memo, useEffect, useMemo, useRef, useState } from "react";

import { segmentStringBySearchTerm } from "app/pages/sales-intelligence/utils/strings";

import { ColumnOptionLabelFragment, StyledColumnOptionLabel, StyledColumnOptionLabelTooltip } from "./styles";

const LABEL_TOOLTIP_OPEN_DELAY_MS = 1000;

type ColumnOptionLabelProps = {
    label: string;
    additionalLabel?: string;
    searchTerm?: string;
};

const ColumnOptionLabelComponent = (props: ColumnOptionLabelProps) => {
    const { label, searchTerm = "", additionalLabel = "" } = props;

    const [isOverflow, setIsOverflow] = useState(false);
    const labelRef = useRef<HTMLDivElement>(null);
    const labelFragments = useMemo(() => segmentStringBySearchTerm(label, searchTerm), [label, searchTerm]);
    const additionalLabelFragments = useMemo(
        () => segmentStringBySearchTerm(additionalLabel, searchTerm),
        [additionalLabel, searchTerm],
    );

    useEffect(() => {
        const lastChild = labelRef.current?.lastElementChild as HTMLElement | undefined | null;
        if (!lastChild) {
            return;
        }

        setIsOverflow(lastChild.offsetWidth < lastChild.scrollWidth);
    }, []);

    return (
        <StyledColumnOptionLabelTooltip
            enabled={isOverflow}
            delay={LABEL_TOOLTIP_OPEN_DELAY_MS}
            content={
                <StyledColumnOptionLabel>
                    <ColumnOptionLabelFragment>{label}</ColumnOptionLabelFragment>
                    {additionalLabel ? (
                        <>
                            <ColumnOptionLabelFragment grayed> | </ColumnOptionLabelFragment>
                            <ColumnOptionLabelFragment
                                grayed
                                ellipsis
                            >
                                {additionalLabel}
                            </ColumnOptionLabelFragment>
                        </>
                    ) : null}
                </StyledColumnOptionLabel>
            }
        >
            <StyledColumnOptionLabel
                ref={(ref) => {
                    labelRef.current = ref;
                }}
                hideOverflow
            >
                {labelFragments.map((fragment, index) => (
                    <ColumnOptionLabelFragment
                        key={index}
                        highlighted={fragment.highlighted}
                    >
                        {fragment.text}
                    </ColumnOptionLabelFragment>
                ))}
                {additionalLabelFragments.length > 0 ? (
                    <ColumnOptionLabelFragment grayed> | </ColumnOptionLabelFragment>
                ) : null}
                {additionalLabelFragments.map((fragment, index, arr) => (
                    <ColumnOptionLabelFragment
                        key={index}
                        highlighted={fragment.highlighted}
                        grayed
                        ellipsis={arr.length - 1 === index}
                    >
                        {fragment.text}
                    </ColumnOptionLabelFragment>
                ))}
            </StyledColumnOptionLabel>
        </StyledColumnOptionLabelTooltip>
    );
};

export const ColumnOptionLabel = memo(ColumnOptionLabelComponent);
